import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/app/hooks";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTranslation } from "react-i18next";
import { postLoginAPI } from "src/store/features/user/login/loginSlice";
import Background from 'src/assets/images/background/background.svg';
import Logo from 'src/assets/images/drugvlab_logo_light_mode.svg';
import CapsyCharacter from 'src/components/CapsyCharacter';

interface LoginPageProps {
  onLoginSuccess: () => void;
}

interface AuthData {
  csrfToken: string;
  username: string;
  loginTime: number;
}

// 로그인 페이지
const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  // 기본 훅
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectedToResetRef = useRef(false);
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [adminApprovalModalOpen, setAdminApprovalModalOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const currentLang = i18n.resolvedLanguage?.startsWith("kr") ? "kr" : "en";
  const userCheckCSRFToken = useAppSelector((state) => state?.userCSRFToken?.csrfToken);

  // 비밀번호 재설정 이동
  useEffect(() => {
    if (redirectedToResetRef.current) return;

    const sid = searchParams.get("sid") || searchParams.get("rid") || "";
    const token = searchParams.get("token") || "";

    if (sid && token) {
      redirectedToResetRef.current = true;
      navigate(`/auth/password-reset/confirm?rid=${encodeURIComponent(sid)}&token=${encodeURIComponent(token)}`, { replace: true });
      return;
    }

    const next = searchParams.get("next") || "";
    if (!next) return;

    try {
      const nextUrl = new URL(next, window.location.origin);
      const nextSid = nextUrl.searchParams.get("sid") || nextUrl.searchParams.get("rid") || "";
      const nextToken = nextUrl.searchParams.get("token") || "";
      if (nextSid && nextToken) {
        redirectedToResetRef.current = true;
        navigate(`/auth/password-reset/confirm?rid=${encodeURIComponent(nextSid)}&token=${encodeURIComponent(nextToken)}`, { replace: true });
      }
    } catch {
      // 잘못된 이동 경로 무시
    }
  }, [location.search, navigate, searchParams]);

  // 저장 인증 초기화
  useEffect(() => {
    // 인증 초기화
    const initializeAuth = async () => {
      try {
        const hasValidSavedAuth = checkSavedAuth();
        if (hasValidSavedAuth) {
          onLoginSuccess();
        } else {
          syncCsrfTokens();
        }
      } catch (error) {
        console.error('인증 초기화 중 오류:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    const timer = setTimeout(initializeAuth, 100);
    return () => clearTimeout(timer);
  }, []);

  // 쿠키값 조회
  const getCookieValue = (name: string): string | null => {
    const value = document.cookie.split('; ').find(row => row.startsWith(`${name}=`))?.split('=')[1];
    return value || null;
  };

  // CSRF 토큰 조회
  const getCsrfTokenFromCookie = (): string | null => {
    for (const name of ['csrftoken', 'csrf_token', 'access_token', 'refresh_token']) {
      const value = getCookieValue(name);
      if (value) return value;
    }
    return null;
  };

  // 세션 쿠키 확인
  const checkSessionCookie = (): boolean => {
    for (const name of ['sessionid', 'session', 'JSESSIONID', 'connect.sid']) {
      if (getCookieValue(name)) return true;
    }
    return false;
  };

  // CSRF 토큰 동기화
  const syncCsrfTokens = (): string | null => {
    const cookieToken = getCsrfTokenFromCookie();
    const localStorageToken = localStorage.getItem('csrftoken');
    const reduxToken = userCheckCSRFToken;
    const validToken = reduxToken || cookieToken || localStorageToken || null;
    if (validToken) {
      if (localStorageToken !== validToken) localStorage.setItem('csrftoken', validToken);
    } else {
      localStorage.removeItem('csrftoken');
      localStorage.removeItem('authData');
    }
    return validToken;
  };

  // 저장 인증 확인
  const checkSavedAuth = (): boolean => {
    try {
      const hasSessionCookie = checkSessionCookie();
      if (!hasSessionCookie) { clearSavedAuth(); return false; }
      const validToken = syncCsrfTokens();
      if (!validToken) return false;
      const savedAuthData = localStorage.getItem('authData');
      if (savedAuthData) {
        const authData: AuthData = JSON.parse(savedAuthData);
        if (authData.csrfToken === validToken) return true;
        else clearSavedAuth();
      }
      return hasSessionCookie && !!validToken;
    } catch (error) {
      console.error('인증 정보 확인 중 오류:', error);
      clearSavedAuth();
      return false;
    }
  };

  // 저장 인증 삭제
  const clearSavedAuth = () => { localStorage.removeItem('authData'); };

  // 로그인 처리
  const handleLogin = async (username: string, password: string, remember: boolean) => {
    setIsSigningIn(true);
    setLoginError(null);
    try {
      await dispatch(postLoginAPI({ username, password, remember })).unwrap();
      const validToken = syncCsrfTokens();
      if (validToken && remember) {
        localStorage.setItem('authData', JSON.stringify({ csrfToken: validToken, username, loginTime: Date.now() }));
      }
      onLoginSuccess();
    } catch (error) {
      console.error('로그인 오류:', error);
      const errorMessage = error?.error || error?.response?.data?.error || error?.message || "An unexpected error occurred";
      if (errorMessage === 'Admin approval required') setAdminApprovalModalOpen(true);
      else if (errorMessage === 'User not found') setLoginError(t("login.errors.userNotFoundWithGuide"));
      else if (error?.name === 'NotAuthorizedException') setLoginError(t("login.errors.incorrectCredentials"));
      else if (error?.name === 'UserNotFoundException') setLoginError(t("login.errors.userNotFound"));
      else if (error?.code === 'UserNotConfirmedException') setLoginError(t("login.errors.userNotConfirmed"));
      else if (error?.name === 'NetworkError') setLoginError(t("login.errors.network"));
      else setLoginError(errorMessage);
    } finally {
      setIsSigningIn(false);
    }
  };

  // 관리자 승인 모달 닫기
  const closeAdminApprovalModal = () => setAdminApprovalModalOpen(false);

  // 비밀번호 표시 전환
  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);

  // 관리자 승인 모달
  const AdminApprovalModal = () => {
    const modalTitle = t("login.adminModal.title");
    const modalMessage = t("login.adminModal.message");
    const modalErrorContext = t("login.adminModal.contact");
    const modalErrorContext2 = t("login.adminModal.notice");

    // 관리자 승인 모달 렌더링
    return (
      <Dialog
        disableScrollLock={false}
        disableAutoFocus={true}
        open={adminApprovalModalOpen}
        onClose={closeAdminApprovalModal}
        aria-labelledby="admin-approval-dialog-title"
        aria-describedby="admin-approval-dialog-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden", boxShadow: "0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)" } }}
      >
        <Box sx={{ px: 3.5, pt: 3.5, pb: 2.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "#eff6ff", border: "1.5px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
            <AccessTimeIcon sx={{ color: "#3b82f6", fontSize: "20px" }} />
          </Box>
          <Typography id="admin-approval-dialog-title" sx={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a", letterSpacing: "-0.02em", lineHeight: 1.3, mb: 0.4 }}>
            {modalTitle}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#64748b", lineHeight: 1.6 }}>Pending administrator review</Typography>
        </Box>
        <Box sx={{ height: 1, bgcolor: "#f1f5f9" }} />
        <DialogContent sx={{ px: 3.5, py: 2.5 }}>
          <Box sx={{ px: 2, py: 1.75, borderRadius: "10px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#1e3a8a", mb: 0.4 }}>Awaiting Approval</Typography>
            <Typography variant="body2" sx={{ color: "#1d4ed8", lineHeight: 1.55 }}>{modalMessage}</Typography>
          </Box>
          <Box sx={{ pl: 0.2 }}>
            {[modalErrorContext, modalErrorContext2].map((item, index) => (
              <Typography key={index} variant="body2" sx={{ color: "#64748b", lineHeight: 1.55, mb: 0.8 }}>- {item}</Typography>
            ))}
          </Box>
        </DialogContent>
        <Box sx={{ height: 1, bgcolor: "#f1f5f9" }} />
        <DialogActions sx={{ px: 3.5, py: 2.5 }}>
          <Button onClick={closeAdminApprovalModal} variant="outlined" autoFocus
            sx={{ borderRadius: "10px", borderColor: "#e2e8f0", color: "#64748b", fontWeight: 600, fontSize: "0.82rem", py: 1.1, px: 2.5, textTransform: "none", "&:hover": { borderColor: "#cbd5e1", background: "#f8fafc" } }}>
            {t("login.adminModal.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Capsy 안내 패널
  const LeftPanel = (
    <Box sx={{
      flex: { md: '0 0 48%' },
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 620,
      p: { md: 4.5, lg: 5.5 },
      borderRadius: '20px',
      color: '#ffffff',
      background: 'linear-gradient(145deg, #4968A2 0%, #4868a3 58%, #2f4675 100%)',
      '&::before': {
        content: '""',
        position: 'absolute',
        right: -96,
        bottom: -120,
        width: 330,
        height: 330,
        borderRadius: '50%',
        border: '42px solid rgba(255,255,255,0.08)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: -80,
        top: -90,
        width: 220,
        height: 220,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.12)',
        filter: 'blur(2px)',
      },
    }}>
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', pt: 2 }}>
        <Typography sx={{
          maxWidth: 430,
          fontSize: 'clamp(0.95rem, 1.25vw, 1.2rem)',
          fontWeight: 800,
          lineHeight: 1.06,
          textShadow: '0 12px 30px rgba(40,28,110,0.24)',
        }}>
          <Box component="span" sx={{ display: 'block', fontSize: '1.8em', fontWeight: 700, mb: 0.5 }}>
            Your
          </Box>
          <Box component="span" sx={{ display: 'block', fontSize: '2.35em', fontWeight: 800 }}>
            AI Drug Discovery
          </Box>
          <Box component="span" sx={{ display: 'block', fontSize: '1.8em', fontWeight: 700, mt: 0.7 }}>
            Companion
          </Box>
        </Typography>
        <Typography sx={{
          mt: 2.5,
          maxWidth: 310,
          color: 'rgba(255,255,255,0.78)',
          fontSize: '0.82rem',
          lineHeight: 1.55,
          fontWeight: 600,
        }}>
          Accelerate your research with AI-powered molecular analysis.
        </Typography>
      </Box>

      <Box sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        flex: 1,
        minHeight: 340,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pb: 3,
      }}>
        <Box sx={{
          width: { md: 470, lg: 540 },
          height: { md: 470, lg: 540 },
          mt: { md: -6, lg: -8 },
        }}>
          <CapsyCharacter />
        </Box>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.78rem', fontWeight: 700 }}>
          Meet Capsy
        </Typography>
        <Typography sx={{ color: '#ffffff', fontSize: '0.8rem', fontWeight: 800 }}>
          www.drugvlab.com
        </Typography>
      </Box>
    </Box>
  );

  // 로그인 입력 패널
  const rightPanelSx = {
    flex: { xs: 1, md: '0 0 52%' },
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    px: { xs: 2, sm: 4, md: 5.5, lg: 6.5 },
    overflowY: 'auto' as const,
    py: { xs: 4, md: 5 },
    background: '#ffffff',
    minHeight: { md: 620 },
  };

  // 페이지 배경
  const pageSx = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: '100vh',
    px: { xs: 2, md: 5 },
    py: { xs: 3, md: 6 },
    backgroundImage: `url(${Background})`,
    backgroundSize: 'cover',
  };

  // 로그인 레이아웃
  const loginShellSx = {
    display: 'flex',
    width: 'min(1400px, 100%)',
    minHeight: { md: 730 },
    background: '#ffffff',
    borderRadius: '28px',
    overflow: 'hidden',
    boxShadow: '0 30px 80px rgba(15,23,42,0.36)',
    p: { xs: 0, md: 3 },
  };

  // 로그인 카드
  const authCardSx = {
    p: 0,
    width: '100%',
    maxWidth: 500,
    boxShadow: 'none',
    background: 'transparent',
  };

  if (isCheckingAuth) {
    return (
      <Box sx={pageSx}>
        <Box sx={loginShellSx}>
          {LeftPanel}
          <Box sx={rightPanelSx}>
            <Paper elevation={0} sx={authCardSx}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 100 }}>
                <Typography variant="h6">{t("login.checkingAuth")}</Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  }

  // 로그인 페이지 렌더링
  return (
    <Box sx={pageSx}>
      <Box sx={loginShellSx}>
        {LeftPanel}
        <Box sx={rightPanelSx}>
          <Paper elevation={0} sx={authCardSx}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 5 }}>
              <Box component="img" src={Logo} alt="DrugVLab Logo" sx={{ width: 230, maxWidth: "60%" }} />
              {/* 언어 변경 */}
              <ToggleButtonGroup
                value={currentLang}
                exclusive
                onChange={(_event, newLang) => { if (newLang !== null) i18n.changeLanguage(newLang).catch(console.error); }}
                aria-label="language toggle"
                sx={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "999px",
                  p: "2px",
                  "& .MuiToggleButtonGroup-grouped": {
                    border: 0, borderRadius: "999px", minWidth: { xs: 46, sm: 50 }, height: 28, px: 1,
                    color: "#64748b", fontSize: "0.72rem", fontWeight: 700, lineHeight: 1, textTransform: "none",
                    "&:not(:first-of-type)": { marginLeft: "2px" },
                    "&.Mui-selected": { backgroundColor: "#f1f5f9", color: "#0f172a", "&:hover": { backgroundColor: "#f1f5f9" } },
                    "&:hover": { backgroundColor: "#f8fafc" },
                  },
                }}
              >
                <ToggleButton value="en">EN</ToggleButton>
                <ToggleButton value="kr">KR</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ mb: 3.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#172033", letterSpacing: "-0.02em" }}>
                {t("login.title")}
              </Typography>
            </Box>

            {loginError && !adminApprovalModalOpen && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoginError(null)}>
                {loginError}
              </Alert>
            )}

          {/* 로그인 제출 */}
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            onSubmit={(e) => {
              e.preventDefault();
              const usernameInput = e.currentTarget.elements.namedItem("username") as HTMLInputElement | null;
              const passwordInput = e.currentTarget.elements.namedItem("password") as HTMLInputElement | null;
              if (!usernameInput || !passwordInput) { setLoginError(t("login.errors.formNotFound")); return; }
              const username = usernameInput.value.trim();
              const password = passwordInput.value.trim();
              if (!username || !password) { setLoginError(t("login.errors.emptyCredentials")); return; }
              handleLogin(username, password, rememberMe);
            }}
          >
            <TextField type="text" name="username" label={t("login.fields.email")} fullWidth required variant="outlined" />
            <TextField
              type={showPassword ? "text" : "password"}
              name="password"
              label={t("login.fields.password")}
              fullWidth required variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="toggle password visibility" onClick={handleTogglePasswordVisibility} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} name="rememberMe" color="primary" />}
              label={t("login.fields.rememberMe")}
            />
            {/* 비밀번호 재설정 */}
            <Button type="button" variant="text" fullWidth onClick={() => navigate("/forgot-password")} sx={{ mt: 1, textTransform: "none" }}>
              {currentLang === "kr" ? "비밀번호 초기화하기" : "Reset your password"}
            </Button>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSigningIn}
              sx={{
                py: 1.5,
                fontWeight: "bold",
                background: "linear-gradient(135deg, #4968A2 0%, #4868a3 58%, #2f4675 100%)",
                boxShadow: "0 14px 28px rgba(73,104,162,0.28)",
                "&:hover": {
                  background: "linear-gradient(135deg, #3f5b8f 0%, #405f98 58%, #263b66 100%)",
                  boxShadow: "0 16px 32px rgba(73,104,162,0.34)",
                },
                "&.Mui-disabled": {
                  background: "#cbd5e1",
                  color: "#ffffff",
                },
              }}
            >
              {isSigningIn ? t("login.buttons.loggingIn") : t("login.buttons.login")}
            </Button>
          </Box>

          {/* 회원가입 이동 */}
          <Button
            onClick={() => navigate("/create-account")}
            type="button"
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontWeight: "bold",
              color: "#ffffff",
              background: "linear-gradient(135deg, #F7AC34 0%, #f4b84d 58%, #d98a12 100%)",
              boxShadow: "0 14px 28px rgba(247,172,52,0.28)",
              "&:hover": {
                background: "linear-gradient(135deg, #e89d25 0%, #eba73a 58%, #c9780d 100%)",
                boxShadow: "0 16px 32px rgba(247,172,52,0.34)",
              },
            }}
          >
            {t("login.buttons.signUp")}
          </Button>
        </Paper>
      </Box>
    </Box>
      <AdminApprovalModal />
    </Box>
  );
};

export default LoginPage;
